<script setup lang="ts">
import type { TableColumn } from '@nuxt/ui'
import { Link, router } from '@inertiajs/vue3'
import { computed, h, ref, resolveComponent } from 'vue'
import { adminInstancePath, bulkAdminInstancesPath } from '@/routes'

interface ChannelOption {
  id: number
  name: string
}

export interface InstanceRow {
  /** Instance id (used for revoking). */
  id: number
  name: string
  identifier?: string | null
  metadata?: Record<string, unknown> | null
  channel_id?: number | null
  reported_version?: string | null
  reported_at?: string | null
  channels?: ChannelOption[]
  created_at?: string | null
  /** Optional link to the related resource (product or customer). */
  to?: string
  /** When provided, renders an Active/Inactive badge. */
  active?: boolean | null
}

const props = withDefaults(
  defineProps<{
    rows: InstanceRow[]
    /** Noun shown in confirmation copy, e.g. "customer" or "product". */
    noun?: string
    emptyText?: string
  }>(),
  {
    noun: 'instance',
    emptyText: 'No instances yet.',
  },
)

const UCheckbox = resolveComponent('UCheckbox')

const rowSelection = ref<Record<string, boolean>>({})
const confirmOpen = ref(false)
const revoking = ref(false)
const pendingIds = ref<number[]>([])

const selectedIds = computed(() =>
  Object.keys(rowSelection.value)
    .filter(key => rowSelection.value[key])
    .map(Number),
)

const hasIdentifiers = computed(() => props.rows.some(row => row.identifier))
const hasMetadata = computed(() => props.rows.some(row => row.metadata && Object.keys(row.metadata).length > 0))
const hasCreatedDates = computed(() => props.rows.some(row => row.created_at))
const hasChannels = computed(() => props.rows.some(row => row.channels && row.channels.length > 0))
const hasReportedStatus = computed(() => props.rows.some(row => row.reported_version || row.reported_at))

const columns = computed<TableColumn<InstanceRow>[]>(() => [
  {
    id: 'select',
    header: ({ table }) =>
      h(UCheckbox, {
        'modelValue': table.getIsAllRowsSelected()
          ? true
          : table.getIsSomeRowsSelected()
            ? 'indeterminate'
            : false,
        'aria-label': 'Select all',
        'onUpdate:modelValue': (value: boolean | 'indeterminate') =>
          table.toggleAllRowsSelected(!!value),
      }),
    cell: ({ row }) =>
      h(UCheckbox, {
        'modelValue': row.getIsSelected(),
        'aria-label': 'Select row',
        'onUpdate:modelValue': (value: boolean | 'indeterminate') =>
          row.toggleSelected(!!value),
      }),
  },
  { accessorKey: 'name', header: 'Name' },
  ...(hasIdentifiers.value ? [{ accessorKey: 'identifier', header: 'Identifier' }] : []),
  ...(hasChannels.value ? [{ accessorKey: 'channel_id', header: 'Channel' }] : []),
  ...(hasReportedStatus.value ? [{ accessorKey: 'reported_version', header: 'Reported' }] : []),
  ...(hasMetadata.value ? [{ accessorKey: 'metadata', header: 'Metadata' }] : []),
  ...(hasCreatedDates.value ? [{ accessorKey: 'created_at', header: 'Created' }] : []),
  { id: 'actions' },
])

function formatMetadata(metadata: Record<string, unknown> | null | undefined) {
  return metadata && Object.keys(metadata).length > 0 ? JSON.stringify(metadata, null, 2) : '—'
}

function formatDate(value: string | null | undefined) {
  return value ? new Date(value).toLocaleDateString() : '—'
}

function channelOptions(row: InstanceRow) {
  return [
    { label: 'Unassigned', value: null },
    ...(row.channels ?? []).map(channel => ({ label: channel.name, value: channel.id })),
  ]
}

function updateChannel(row: InstanceRow, channelId: number | null) {
  router.patch(adminInstancePath(row.id), { channel_id: channelId }, { preserveScroll: true })
}

function requestRevoke(ids: number[]) {
  if (ids.length === 0)
    return

  pendingIds.value = ids
  confirmOpen.value = true
}

function confirmRevoke() {
  router.delete(bulkAdminInstancesPath(), {
    data: { ids: pendingIds.value },
    preserveScroll: true,
    onStart: () => (revoking.value = true),
    onFinish: () => {
      revoking.value = false
      confirmOpen.value = false
      pendingIds.value = []
    },
    onSuccess: () => {
      rowSelection.value = {}
    },
  })
}
</script>

<template>
  <div class="flex flex-col gap-3">
    <Transition
      enter-active-class="transition"
      enter-from-class="opacity-0 -translate-y-1"
      leave-active-class="transition"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="selectedIds.length > 0"
        class="border-default bg-elevated/50 flex items-center justify-between gap-3 rounded-lg border px-4 py-2"
      >
        <span class="text-highlighted text-sm font-medium">
          {{ selectedIds.length }} selected
        </span>
        <div class="flex items-center gap-2">
          <UButton
            label="Clear"
            color="neutral"
            variant="ghost"
            size="sm"
            @click="rowSelection = {}"
          />
          <UButton
            label="Revoke"
            icon="i-lucide-x"
            color="error"
            variant="subtle"
            size="sm"
            @click="requestRevoke(selectedIds)"
          />
        </div>
      </div>
    </Transition>

    <div
      v-if="rows.length === 0"
      class="ring-default text-muted rounded-lg p-6 text-center text-sm ring"
    >
      {{ emptyText }}
    </div>

    <UTable
      v-else
      v-model:row-selection="rowSelection"
      :data="rows"
      :columns="columns"
      :get-row-id="(row: InstanceRow) => String(row.id)"
      class="ring-default rounded-lg ring"
    >
      <template #name-cell="{ row }">
        <Link
          v-if="row.original.to"
          :href="row.original.to"
          class="text-highlighted font-medium hover:underline"
        >
          {{ row.original.name }}
        </Link>
        <span v-else class="text-highlighted font-medium">
          {{ row.original.name }}
        </span>
      </template>

      <template #identifier-cell="{ row }">
        <span class="font-mono text-sm">
          {{ row.original.identifier || '—' }}
        </span>
      </template>

      <template #channel_id-cell="{ row }">
        <USelectMenu
          :model-value="row.original.channel_id ?? null"
          :items="channelOptions(row.original)"
          value-key="value"
          size="xs"
          class="min-w-40"
          @update:model-value="updateChannel(row.original, $event)"
        />
      </template>

      <template #reported_version-cell="{ row }">
        <div class="text-sm">
          <div>{{ row.original.reported_version || '—' }}</div>
          <div class="text-muted text-xs">
            {{ formatDate(row.original.reported_at) }}
          </div>
        </div>
      </template>

      <template #metadata-cell="{ row }">
        <pre class="text-toned max-w-sm overflow-x-auto text-xs whitespace-pre-wrap">{{ formatMetadata(row.original.metadata) }}</pre>
      </template>

      <template #created_at-cell="{ row }">
        {{ formatDate(row.original.created_at) }}
      </template>

      <template #actions-header>
        <span class="sr-only">Actions</span>
      </template>

      <template #actions-cell="{ row }">
        <div class="flex items-center justify-end gap-2">
          <UBadge
            v-if="row.original.active != null"
            :color="row.original.active ? 'success' : 'neutral'"
            variant="subtle"
            size="sm"
          >
            {{ row.original.active ? 'Active' : 'Inactive' }}
          </UBadge>
          <UButton
            :to="adminInstancePath(row.original.id)"
            :as="Link"
            icon="i-lucide-activity"
            color="neutral"
            variant="ghost"
            size="sm"
            square
            aria-label="View health"
          />
          <UButton
            icon="i-lucide-x"
            color="neutral"
            variant="ghost"
            size="sm"
            square
            aria-label="Revoke"
            @click="requestRevoke([row.original.id])"
          />
        </div>
      </template>
    </UTable>

    <UModal
      v-model:open="confirmOpen"
      title="Revoke access"
      :description="`Revoke ${pendingIds.length} ${pendingIds.length === 1 ? noun : `${noun}s`}? Access can be granted again later.`"
    >
      <template #footer>
        <div class="flex justify-end gap-3">
          <UButton
            label="Cancel"
            color="neutral"
            variant="ghost"
            @click="confirmOpen = false"
          />
          <UButton
            label="Revoke"
            color="error"
            icon="i-lucide-x"
            :loading="revoking"
            @click="confirmRevoke"
          />
        </div>
      </template>
    </UModal>
  </div>
</template>
